import { ArrowPathIcon, CheckIcon } from '@heroicons/react/24/outline';

import { Alert, Button, Spinner } from '../../components/ui';
import { OpportunityResultRow, ScoringState } from './api';
import { gainLabels, gainWeights, painLabels, painWeights } from './publicScoring';

interface SaveRevisionBarProps {
  hasChanges: boolean;
  isSaving: boolean;
  saveError?: string | null;
  onSave: () => void;
}

export function SaveRevisionBar({ hasChanges, isSaving, saveError, onSave }: SaveRevisionBarProps) {
  if (!hasChanges && !isSaving && !saveError) return null;

  return (
    <div className="sticky bottom-0 z-30 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur dark:border-dark-border dark:bg-dark-surface/95">
      <div className="mx-auto flex max-w-7xl flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="font-semibold text-charcoal dark:text-gray-100">
            {hasChanges ? 'Unsaved changes to your view' : 'Your saved view is up to date'}
          </div>
          <p className="text-small text-gray-500 dark:text-gray-400">
            Detail and score edits are saved to your view only.
          </p>
        </div>
        {saveError && <Alert variant="error">{saveError}</Alert>}
        <Button onClick={onSave} disabled={!hasChanges || isSaving}>
          {isSaving ? <Spinner size="sm" /> : hasChanges ? <ArrowPathIcon className="h-4 w-4" /> : <CheckIcon className="h-4 w-4" />}
          {isSaving ? 'Saving...' : 'Save My View'}
        </Button>
      </div>
    </div>
  );
}

export function TopTenCallout({ rows }: { rows: OpportunityResultRow[] }) {
  const topRows = rows.filter((row) => row.is_top_10_active).slice(0, 10);
  return (
    <section className="px-4 pb-8 pt-2">
      <div className="mx-auto max-w-7xl rounded-lg border border-[#F2967B] bg-[#FFF1EA] p-5 dark:border-orange-900/60 dark:bg-orange-950/20">
        <h2 className="text-body font-bold text-[#B8371E] dark:text-orange-200">
          Top 10 for Leadership Conversation
        </h2>
        <p className="mt-3 text-small leading-6 text-charcoal dark:text-gray-200">
          Default sort surfaces the 10 active opportunities with the highest priority. Highlighted in peach below. Use
          filters above the table to isolate strategic objectives, sources, and themes before deciding which assumptions
          need challenge.
        </p>
        <p className="mt-4 border-t border-[#F2967B]/50 pt-4 text-small italic leading-6 text-charcoal dark:text-gray-200">
          <strong>Leadership questions:</strong> Which top opportunities are already committed? Which workshop candidates
          have not been weighed yet? Which gain or pain factors should be challenged before sharing a final prioritization?
        </p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2 lg:grid-cols-5">
          {topRows.map((row) => (
            <div key={row.id} className="flex min-w-0 items-center gap-2 rounded-md border border-[#F6B28D] bg-white/70 px-2 py-1.5 dark:border-orange-900/40 dark:bg-dark-surface">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#F2967B] text-micro font-bold text-white ring-2 ring-[#1A2540]">
                {row.active_rank}
              </span>
              <span className="min-w-0 flex-1 truncate text-micro font-semibold text-charcoal dark:text-gray-100">
                {row.name}
              </span>
              <span className="shrink-0 text-micro font-bold text-eliza-red">
                {formatScore(priorityValue(row))}{row.priority_score_source === 'source_indexes' ? '*' : ''}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function CompactOpportunityReadout({
  rows,
  totalCount,
  onSelect,
}: {
  rows: OpportunityResultRow[];
  totalCount: number;
  onSelect: (id: string) => void;
}) {
  return (
    <section className="bg-gray-50 px-4 pb-7 dark:bg-dark-bg">
      <div className="mx-auto max-w-7xl rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
        <div className="mb-4 flex flex-wrap items-center gap-x-6 gap-y-2 border-b border-dashed border-gray-200 pb-4 text-small dark:border-dark-border">
          <span>
            <strong className="text-eliza-red">{rows.length}</strong>{' '}
            <span className="text-gray-500 dark:text-gray-400">shown of</span>{' '}
            <strong className="text-eliza-red">{totalCount}</strong>{' '}
            <span className="text-gray-500 dark:text-gray-400">total</span>
          </span>
          <span className="text-gray-500 dark:text-gray-400">Top 10 highlighted in peach</span>
          <span className="ml-auto inline-flex items-center gap-2 text-micro text-gray-500 dark:text-gray-400">
            <span className="h-3 w-3 rounded-full bg-[#F2967B] ring-2 ring-[#1A2540]" />
            Top 10
          </span>
        </div>
        <ol className="grid gap-x-7 gap-y-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          {rows.map((row) => (
            <li key={row.id}>
              <button
                type="button"
                onClick={() => onSelect(row.id)}
                className="group flex w-full min-w-0 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-eliza-red"
              >
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-micro font-bold text-white ${
                  row.is_top_10_active ? 'bg-[#F2967B] ring-2 ring-[#1A2540]' : 'bg-[#F2967B]/85'
                }`}>
                  {row.active_rank ?? row.rank ?? '—'}
                </span>
                <span className="min-w-0 flex-1 truncate text-micro font-medium text-charcoal group-hover:text-eliza-red dark:text-gray-100">
                  {row.id} · {row.name}
                </span>
                <span className={`shrink-0 text-micro font-bold ${priorityValue(row) >= 20 ? 'text-eliza-red' : 'text-gray-500 dark:text-gray-400'}`}>
                  {formatScore(priorityValue(row))}{row.priority_score_source === 'source_indexes' ? '*' : ''}
                </span>
              </button>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function ScoringMethodology({ state }: { state: ScoringState }) {
  const gainFactorCount = gainLabels(state).length;
  const painFactorCount = painLabels(state).length;
  return (
    <section className="px-4 py-6">
      <div className="mx-auto max-w-7xl rounded-lg border border-gray-200 bg-white p-5 dark:border-dark-border dark:bg-dark-surface">
        <details>
          <summary className="cursor-pointer font-subtitle text-h3 text-charcoal dark:text-gray-100">
            Scoring methodology · {gainFactorCount} gain factors, {painFactorCount} pain factors
          </summary>
          <p className="mt-3 text-small leading-6 text-gray-600 dark:text-gray-300">
            Each opportunity is scored 1 to 5 on each factor. Weighted gain and pain are normalized to 0-100 indexes.
            Priority is 70% gain index plus 30% feasibility, where feasibility is 100 minus pain index. Asterisks mark
            priorities derived from uploaded gain/pain indexes.
          </p>
          <div className="mt-4 grid gap-4 md:grid-cols-2">
            <FactorList title="Gain factors" labels={gainLabels(state)} weights={gainWeights(state)} />
            <FactorList title="Pain factors" labels={painLabels(state)} weights={painWeights(state)} />
          </div>
        </details>
      </div>
    </section>
  );
}

function FactorList({ title, labels, weights }: { title: string; labels: string[]; weights: number[] }) {
  return (
    <div>
      <h3 className="mb-2 text-small font-semibold text-charcoal dark:text-gray-100">{title}</h3>
      <div className="divide-y divide-gray-100 rounded-md border border-gray-200 dark:divide-dark-border dark:border-dark-border">
        {labels.map((label, index) => (
          <div key={label} className="grid grid-cols-[60px_minmax(0,1fr)] gap-3 px-3 py-2 text-small">
            <span className="font-semibold text-eliza-red">{Math.round((weights[index] ?? 0) * 100)}%</span>
            <span className="text-gray-700 dark:text-gray-200">{label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function priorityValue(row: OpportunityResultRow) {
  return row.priority_score ?? row.composite_score;
}

function formatScore(value: number) {
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
}
